/**
 * NumbersViewer -- read-only render of a numbers_submissions snapshot.
 *
 * Used on InterviewReview (recruiter) + MockReview (candidate) pages.
 *
 * Snapshot shape (mirrors interview_sessions.numbers_submissions JSONB):
 *   {
 *     rows: number,
 *     cols: number,
 *     cells: { "A1": { raw, value }, ... },
 *     summary: string,
 *     cell_count: number,
 *     updated_at_ms: number
 *   }
 */
import React, { useMemo } from 'react';
import { Calculator } from 'lucide-react';

interface NumbersSnapshot {
  rows?: number;
  cols?: number;
  cells?: Record<string, { raw?: string; value?: string | number | null }>;
  summary?: string;
  cell_count?: number;
  updated_at_ms?: number;
}

interface NumbersViewerProps {
  snapshot: NumbersSnapshot;
  lang: 'de' | 'en';
  height?: number;
}

function colLabel(idx: number): string {
  let s = '';
  let n = idx;
  while (true) {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
    if (n < 0) break;
  }
  return s;
}

function formatValue(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'string') return v;
  if (!Number.isFinite(v)) return '';
  const abs = Math.abs(v);
  if (abs !== 0 && (abs >= 1e9 || abs < 1e-4)) return v.toExponential(3);
  const n = Number(v.toPrecision(6));
  return Number.isInteger(n) ? n.toString() : n.toString();
}

const NumbersViewer: React.FC<NumbersViewerProps> = ({ snapshot, lang, height = 360 }) => {
  const rows = snapshot.rows ?? 12;
  const cols = snapshot.cols ?? 5;
  const cells = snapshot.cells ?? {};

  const t = useMemo(() => ({
    none:    lang === 'de' ? '(keine Daten)' : '(no data)',
    cells:   lang === 'de' ? 'belegte Zellen' : 'cells filled',
  }), [lang]);

  const cellCount = Object.keys(cells).length;

  return (
    <div className="bg-white/[0.02] rounded-2xl border border-white/10 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 bg-white/[0.02]">
        <div className="flex items-center gap-2">
          <Calculator className="w-4 h-4 text-white/50" />
          <span className="text-[11px] uppercase tracking-wider text-white/55 font-semibold">
            Numbers
          </span>
        </div>
        <span className="text-[10px] text-white/40">
          {cellCount} {t.cells}
        </span>
      </div>

      <div className="overflow-auto" style={{ maxHeight: height }}>
        {cellCount === 0 ? (
          <div className="px-4 py-6 text-center text-white/35 text-sm">{t.none}</div>
        ) : (
          <table className="w-full border-separate border-spacing-0 text-[12px]">
            <thead className="sticky top-0 z-10">
              <tr>
                <th className="bg-white/[0.04] text-white/40 text-[10px] font-medium border-r border-b border-white/10 w-10 px-1 py-1.5">
                  &nbsp;
                </th>
                {Array.from({ length: cols }, (_, c) => (
                  <th
                    key={c}
                    className="bg-white/[0.04] text-white/45 text-[10px] font-semibold border-r border-b border-white/10 px-2 py-1.5 min-w-[100px]"
                  >
                    {colLabel(c)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: rows }, (_, r) => (
                <tr key={r}>
                  <td className="bg-white/[0.03] text-white/40 text-[10px] font-medium border-r border-b border-white/10 text-center w-10 px-1 py-1">
                    {r + 1}
                  </td>
                  {Array.from({ length: cols }, (_, c) => {
                    const addr = `${colLabel(c)}${r + 1}`;
                    const cell = cells[addr];
                    const raw = cell?.raw ?? '';
                    const value = cell?.value;
                    const isFormula = typeof raw === 'string' && raw.startsWith('=');
                    const display = isFormula
                      ? formatValue(value)
                      : raw || formatValue(value);
                    return (
                      <td
                        key={c}
                        title={isFormula ? `${raw}  =  ${formatValue(value)}` : undefined}
                        className={[
                          'border-r border-b border-white/8 px-2 py-1',
                          cell ? 'bg-white/[0.02]' : 'bg-transparent',
                        ].join(' ')}
                      >
                        <span
                          className={[
                            'block truncate',
                            isFormula
                              ? typeof value === 'number'
                                ? 'text-emerald-300/90 font-mono text-right'
                                : 'text-rose-300/80 font-mono text-right'
                              : !Number.isNaN(Number(raw)) && raw !== ''
                                ? 'text-white/90 font-mono text-right'
                                : 'text-white/85',
                          ].join(' ')}
                        >
                          {display || <span className="text-white/15">·</span>}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default NumbersViewer;
